import { Menu, MenuItem } from '@mui/material';
import { MouseEvent, useCallback } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { Tooltip } from '@customink/pigment-react';
import { FeedbackPath } from '~/config';
import setLocation from '~/adapters/browser/setLocation';
import { usePreservedQueryParam } from '~/adapters/browser/usePreservedQueryParam';
import { useLinks } from '~/contexts/LinkContext';
import { EmptyFunction } from '~/adapters/typescript/emptyFunction';
import { Design } from '~/adapters/services/accounts/designs/types';
import { trackEvent } from '~/adapters/tracking/tracking';
import { gaCategoryOld } from '../tracking';
import { SharingStatusType } from '../SharingAndCollab/types';

interface CardMenuProps {
    handleClose: EmptyFunction;
    anchorElement: HTMLElement | null;
    design: Design;
    designSharingStatus: SharingStatusType;
    resourceId?: string;
    onDesignDeleteClick: EmptyFunction;
}

export function CardMenu({
    handleClose,
    anchorElement,
    resourceId,
    design,
    designSharingStatus,
    onDesignDeleteClick,
}: CardMenuProps) {
    const { rfeLink, fundRaisingStart } = useLinks();
    const history = useHistory();
    const queryParams = usePreservedQueryParam();

    const deleteDesign = useCallback(() => {
        handleClose();
        trackEvent({
            category: gaCategoryOld,
            action: 'Button Click',
            label: 'Delete Design',
        });
        onDesignDeleteClick();
    }, [handleClose, onDesignDeleteClick]);

    const navigateToFeedback = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            // This will always be true because it's only fired when designSharingStatus is not private
            if (resourceId) {
                handleClose();
                e.preventDefault();
                trackEvent({
                    category: gaCategoryOld,
                    action: 'Link Click',
                    label: 'View Feedback',
                });
                history.push(`${generatePath(FeedbackPath, { resourceId })}${queryParams}`);
            }
        },
        [resourceId, handleClose, history, queryParams],
    );
    const navigateToGroupOrders = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            handleClose();
            e.preventDefault();
            trackEvent({
                category: gaCategoryOld,
                action: 'Link Click',
                label: 'Set up Group Order',
            });
            setLocation(`${rfeLink}/g/${design.compositeId}/activity`);
        },
        [handleClose, setLocation, rfeLink, design.compositeId],
    );

    const navigateToFundraiser = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            handleClose();
            e.preventDefault();
            trackEvent({
                category: gaCategoryOld,
                action: 'Link Click',
                label: 'Start a Fundraiser',
            });
            setLocation(`${fundRaisingStart}?cid=${design.compositeId}&design_from_ci=true`);
        },
        [handleClose, setLocation, fundRaisingStart, design.compositeId],
    );

    return (
        <Menu
            id="more-menu"
            anchorEl={anchorElement}
            open={Boolean(anchorElement)}
            onClose={handleClose}
            MenuListProps={{
                'aria-labelledby': 'more-menu',
            }}
        >
            {designSharingStatus !== 'private' && <MenuItem onClick={navigateToFeedback}>View feedback</MenuItem>}
            <MenuItem onClick={navigateToGroupOrders}>
                {design.usedInGroupOrderForm ? 'Manage Group Order' : 'Set up Group Order'}
            </MenuItem>
            <MenuItem onClick={navigateToFundraiser}>Start a Fundraiser</MenuItem>
            <Tooltip title={design.isLocked ? 'This design can’t be deleted' : ''} placement="top">
                <span data-testid="delete design menuitem">
                    <MenuItem onClick={deleteDesign} disabled={design.isLocked}>
                        Delete
                    </MenuItem>
                </span>
            </Tooltip>
        </Menu>
    );
}
